<template>
    <v-menu
        v-if="userStore.isAuthenticated"
        v-model="menuOpen"
        offset-y
        :location="showLabel ? 'top end' : 'bottom end'"
        :close-on-content-click="false"
        transition="slide-y-transition"
    >
        <template v-slot:activator="{ props }">
            <!-- Button with label -->
            <v-btn
                v-if="showLabel"
                v-bind="props"
                variant="flat"
                class="queue-btn-with-label"
                style="background-color: var(--color-tertiary); border-radius: 8px;"
            >
                <div class="queue-btn-icon-wrap" :class="{ 'queue-btn-icon-wrap--processing': isProcessing }">
                    <v-icon size="20">mdi-bell-outline</v-icon>
                    <span v-if="isProcessing" class="queue-badge-spinner">
                        <v-progress-circular
                            indeterminate
                            :size="processingCount > 0 ? 22 : 18"
                            :width="processingCount > 0 ? 2 : 2.5"
                            :color="primaryColor"
                        >
                            <span v-if="processingCount > 0" class="queue-active-count">{{ processingCount }}</span>
                        </v-progress-circular>
                    </span>
                    <span v-else-if="unreadCount > 0" class="queue-badge-count">{{ unreadCount }}</span>
                </div>
                <span class="queue-label-text">{{ t('components.common.queueStatus.title') }}</span>
            </v-btn>
            <!-- Icon only button -->
            <v-btn
                v-else
                v-bind="props"
                variant="text"
                icon
                class="queue-btn"
                :title="t('components.common.queueStatus.title')"
                size="small"
            >
                <div class="queue-btn-icon-wrap" :class="{ 'queue-btn-icon-wrap--processing': isProcessing }">
                    <v-icon size="20" class="queue-icon">mdi-bell-outline</v-icon>
                    <span v-if="isProcessing" class="queue-badge-spinner">
                        <v-progress-circular
                            indeterminate
                            :size="processingCount > 0 ? 22 : 18"
                            :width="processingCount > 0 ? 2 : 2.5"
                            :color="primaryColor"
                        >
                            <span v-if="processingCount > 0" class="queue-active-count">{{ processingCount }}</span>
                        </v-progress-circular>
                    </span>
                    <span v-else-if="unreadCount > 0" class="queue-badge-count">{{ unreadCount }}</span>
                </div>
            </v-btn>
        </template>

        <v-card max-width="450" class="queue-card">
            <div class="queue-header">
                <span class="queue-header-title">{{ t('components.common.queueStatus.title') }}</span>
                <div class="queue-header-actions">
                    <v-tooltip location="bottom" :text="t('components.common.queueStatus.buttons.markAllRead')">
                        <template v-slot:activator="{ props: tooltipProps }">
                            <v-btn
                                v-bind="tooltipProps"
                                v-if="unreadCount > 0"
                                icon="mdi-email-check-outline"
                                variant="text"
                                @click="handleMarkAllAsRead"
                                :loading="isMarkingAllRead"
                                :disabled="isMarkingAllRead"
                                class="queue-header-btn"
                                size="small"
                            ></v-btn>
                        </template>
                    </v-tooltip>
                    <v-tooltip location="bottom" :text="t('components.common.queueStatus.buttons.clearFinished')">
                        <template v-slot:activator="{ props: tooltipProps }">
                            <v-btn
                                v-bind="tooltipProps"
                                v-if="finishedJobs.length > 0"
                                icon="mdi-notification-clear-all"
                                variant="text"
                                @click="handleClearAll"
                                :loading="isClearing"
                                :disabled="isClearing"
                                class="queue-header-btn"
                                size="small"
                            ></v-btn>
                        </template>
                    </v-tooltip>
                    <v-btn
                        icon="mdi-refresh"
                        variant="text"
                        @click="userStore.fetchProcessingQueue"
                        :loading="userStore.isLoadingQueue"
                        :title="t('components.common.queueStatus.buttons.refresh')"
                        class="queue-header-btn"
                        size="small"
                    ></v-btn>
                </div>
            </div>

            <v-list v-if="queueItems.length > 0 || exportEntries.length > 0 || batchedEntries.length > 0" density="compact" class="py-0 queue-list">
                <!-- ESPERANDO O CLIENTE — PRIMEIRO DE TODOS.
                     Esta é a única seção do sino em que a bola está com quem
                     está lendo; tudo o mais é relato do que o sistema faz
                     sozinho. Antes disto a peça ficava em "Em processamento"
                     indefinidamente: o front dizia que o sistema trabalhava
                     enquanto o sistema esperava a pessoa. -->
                <template v-if="awaitingClientJobs.length > 0">
                    <div class="queue-section-label queue-section-label--awaiting">
                        {{ t('components.common.queueStatus.sections.awaitingClient') }}
                    </div>
                    <v-list-item
                        v-for="job in awaitingClientJobs"
                        :key="`awaiting-${job.id}`"
                        class="queue-item queue-item--awaiting"
                    >
                        <template v-slot:prepend>
                            <v-icon size="18" color="primary" class="queue-item-icon">mdi-account-edit-outline</v-icon>
                        </template>

                        <v-list-item-title class="queue-item-name mp-sensitive" :title="job.experiment_name">
                            {{ job.experiment_name }}
                        </v-list-item-title>

                        <v-list-item-subtitle class="queue-item-status">
                            <span class="text-primary">{{ t('components.common.queueStatus.status.awaitingClient') }}</span>
                        </v-list-item-subtitle>

                        <template v-slot:append>
                            <DSButton
                                variant="primary"
                                size="x-small"
                                class="queue-view-btn"
                                @click="continueWizard(job)"
                            >
                                {{ t('components.common.queueStatus.buttons.continueWizard') }}
                            </DSButton>
                        </template>
                    </v-list-item>
                </template>

                <!-- Report exports, one row per creative. Several can be in flight
                     at once since SYN-1642, so this is a list and not one item. -->
                <template v-if="exportEntries.length > 0">
                    <div class="queue-section-label">{{ t('components.common.queueStatus.sections.export') }}</div>
                    <template v-for="entry in exportEntries" :key="entry.experimentId">
                        <v-list-item
                            class="queue-item"
                            :class="{ 'queue-item--active': entry.active }"
                        >
                            <template v-slot:prepend>
                                <v-progress-circular
                                    v-if="entry.active"
                                    :model-value="entry.progress"
                                    size="28"
                                    width="2.5"
                                    color="primary"
                                    class="queue-item-icon"
                                >
                                    <span class="export-pct">{{ Math.round(entry.progress) }}</span>
                                </v-progress-circular>
                                <!-- SYN-2111: sem ícone próprio a linha do HTML e a
                                     do .pptx ficariam indistinguíveis. -->
                                <v-icon v-else-if="entry.result === 'success' && entry.destination === 'html'" size="18" color="success" class="queue-item-icon">mdi-language-html5</v-icon>
                                <v-icon v-else-if="entry.result === 'success'" size="18" color="success" class="queue-item-icon">mdi-file-check-outline</v-icon>
                                <v-icon v-else-if="entry.result === 'error'" size="18" color="error" class="queue-item-icon">mdi-alert-circle-outline</v-icon>
                                <v-icon v-else size="18" color="grey-lighten-1" class="queue-item-icon">mdi-cancel</v-icon>
                            </template>

                            <v-list-item-title class="queue-item-name mp-sensitive" :title="entry.experimentName">
                                {{ entry.experimentName || t('components.common.queueStatus.export.defaultName') }}
                            </v-list-item-title>

                            <v-list-item-subtitle class="queue-item-status">
                                <span v-if="entry.active" class="text-primary">{{ t('components.common.queueStatus.status.exporting', { progress: Math.round(entry.progress) }) }}</span>
                                <span v-else-if="entry.result === 'success' && entry.slidesUrl" class="text-grey">{{ t('components.common.queueStatus.status.exportedSlides') }}</span>
                                <span v-else-if="entry.result === 'success' && entry.destination === 'html'" class="text-grey">{{ t('components.common.queueStatus.status.exportedHtml') }}</span>
                                <span v-else-if="entry.result === 'success'" class="text-grey">{{ t('components.common.queueStatus.status.exported') }}</span>
                                <span v-else-if="entry.result === 'error'" class="text-error">{{ t('components.common.queueStatus.status.exportFailed') }}</span>
                                <span v-else class="text-grey">{{ t('components.common.queueStatus.status.exportCancelled') }}</span>
                            </v-list-item-subtitle>

                            <template v-slot:append>
                                <DSButton
                                    v-if="entry.active"
                                    variant="secondary"
                                    size="x-small"
                                    class="queue-view-btn"
                                    @click="cancelExport(entry.experimentId)"
                                >
                                    {{ t('components.common.queueStatus.buttons.cancelOrphan') }}
                                </DSButton>
                                <!-- Only when the render finished while this tab was
                                     closed: a live run already downloaded the file. -->
                                <DSButton
                                    v-if="!entry.active && entry.pendingUrl"
                                    variant="secondary"
                                    size="x-small"
                                    class="queue-view-btn"
                                    @click="downloadPendingExport(entry.experimentId)"
                                >
                                    {{ t('components.common.queueStatus.buttons.downloadExport') }}
                                </DSButton>
                                <!-- Google Slides destination: the deck lives in the
                                     Drive, so the action is to open it, not to save. -->
                                <DSButton
                                    v-if="!entry.active && entry.slidesUrl"
                                    variant="secondary"
                                    size="x-small"
                                    class="queue-view-btn"
                                    @click="openPendingSlidesExport(entry.experimentId)"
                                >
                                    {{ t('components.common.queueStatus.buttons.openSlides') }}
                                </DSButton>
                                <!-- SYN-2721: o relatório HTML abre a página pública. -->
                                <DSButton
                                    v-if="!entry.active && entry.publicUrl"
                                    variant="secondary"
                                    size="x-small"
                                    class="queue-view-btn"
                                    @click="openPendingHtmlExport(entry.experimentId)"
                                >
                                    {{ t('components.common.queueStatus.buttons.openReport') }}
                                </DSButton>
                                <v-btn
                                    v-if="!entry.active"
                                    icon="mdi-close"
                                    variant="text"
                                    @click="dismissExport(entry.experimentId)"
                                    :title="t('components.common.queueStatus.buttons.dismiss')"
                                    class="dismiss-btn"
                                    size="small"
                                ></v-btn>
                            </template>
                        </v-list-item>
                        <div v-if="needsTranscodeNotice(entry)" class="export-transcode-note">
                            <v-icon size="14" class="export-transcode-note__icon">mdi-information-outline</v-icon>
                            <span>{{ t('components.common.queueStatus.export.transcodeWarning') }}</span>
                        </div>
                    </template>
                </template>

                <!-- Orphan Jobs (awaiting_upload after connection drop) -->
                <template v-if="orphanJobs.length > 0">
                    <div class="queue-section-label queue-section-label--warning">
                        {{ t('components.common.queueStatus.sections.orphan') }}
                    </div>
                    <v-list-item
                        v-for="job in orphanJobs"
                        :key="job.id"
                        class="queue-item queue-item--orphan"
                    >
                        <template v-slot:prepend>
                            <v-icon size="18" color="warning" class="queue-item-icon">mdi-alert-circle-outline</v-icon>
                        </template>

                        <v-list-item-title class="queue-item-name mp-sensitive" :title="job.experiment_name">
                            {{ job.experiment_name }}
                        </v-list-item-title>

                        <v-list-item-subtitle class="queue-item-status text-warning">
                            {{ t('components.common.queueStatus.status.orphan') }}
                        </v-list-item-subtitle>

                        <template v-slot:append>
                            <DSButton
                                variant="secondary"
                                size="x-small"
                                class="queue-view-btn"
                                :loading="cancellingId === job.id"
                                :disabled="!!cancellingId"
                                @click="handleCancelOrphan(job)"
                            >
                                {{ t('components.common.queueStatus.buttons.cancelOrphan') }}
                            </DSButton>
                        </template>
                    </v-list-item>
                </template>

                <!-- Batch Groups -->
                <template v-if="batchedEntries.length > 0">
                    <div class="queue-section-label">{{ t('components.common.queueStatus.sections.batches') }}</div>
                    <template v-for="batch in batchedEntries" :key="batch.batchId">
                        <!-- Batch header row -->
                        <v-list-item
                            class="queue-item queue-item--batch"
                            :class="{ 'queue-item--active': !batch.allTerminal, 'queue-item--unread': batch.allTerminal && batch.jobs.some(j => !j.read) }"
                            @click="toggleBatch(batch.batchId)"
                            style="cursor: pointer"
                        >
                            <template v-slot:prepend>
                                <v-progress-circular
                                    v-if="!batch.allTerminal"
                                    indeterminate
                                    size="18"
                                    width="2"
                                    color="primary"
                                    class="queue-item-icon"
                                ></v-progress-circular>
                                <v-icon
                                    v-else-if="batch.failedCount > 0"
                                    size="18"
                                    color="warning"
                                    class="queue-item-icon"
                                >mdi-alert-circle-outline</v-icon>
                                <!-- SYN-962: nada concluído (só cancelados/abandonados)
                                     → ícone neutro cinza, nunca o check verde de sucesso. -->
                                <v-icon
                                    v-else-if="batch.completedCount === 0"
                                    size="18"
                                    color="grey-lighten-1"
                                    class="queue-item-icon"
                                >mdi-cancel</v-icon>
                                <v-icon
                                    v-else
                                    size="18"
                                    color="success"
                                    class="queue-item-icon"
                                >mdi-check-circle</v-icon>
                            </template>

                            <v-list-item-title class="queue-item-name">
                                <span v-if="!batch.allTerminal">
                                    {{ t('components.common.queueStatus.batch.header', {
                                        time: formatBatchTime(batch.created_at),
                                        ok: batch.completedCount,
                                        processing: batch.activeCount,
                                        failed: batch.failedCount,
                                    }) }}
                                </span>
                                <span v-else>
                                    {{ batchDoneSummary(batch) }}
                                </span>
                            </v-list-item-title>

                            <template v-slot:append>
                                <DSButton
                                    v-if="batch.allTerminal && batch.failedCount > 0"
                                    variant="secondary"
                                    size="x-small"
                                    class="queue-view-btn"
                                    :loading="retryingBatchId === batch.batchId"
                                    :disabled="!!retryingBatchId"
                                    @click.stop="handleRetryBatch(batch.batchId)"
                                >
                                    {{ t('components.common.queueStatus.buttons.retryBatchFailed') }}
                                </DSButton>
                                <v-btn
                                    icon="mdi-close"
                                    variant="text"
                                    @click.stop="handleDismissBatch(batch)"
                                    :loading="dismissingBatchId === batch.batchId"
                                    :disabled="!!dismissingBatchId"
                                    :title="t('components.common.queueStatus.buttons.dismiss')"
                                    class="dismiss-btn"
                                    size="small"
                                ></v-btn>
                                <v-icon size="16" class="ml-1" color="grey">
                                    {{ expandedBatches.has(batch.batchId) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                </v-icon>
                            </template>
                        </v-list-item>

                        <!-- Expanded batch job list -->
                        <template v-if="expandedBatches.has(batch.batchId)">
                            <v-list-item
                                v-for="job in batch.jobs"
                                :key="job.id"
                                class="queue-item queue-item--batch-child"
                                :class="{
                                    'queue-item--active': ['queued','pending','validating','processing'].includes(job.status),
                                    'queue-item--unread': (job.status === 'completed' || job.status === 'failed') && !job.read,
                                }"
                            >
                                <template v-slot:prepend>
                                    <!-- Esperando o cliente vence o status cru do
                                         job: o job PODE estar `completed` (a
                                         análise que não depende do wizard
                                         terminou) e a peça continuar precisando
                                         de alguém. Um check verde aqui diria que
                                         não falta nada. -->
                                    <v-icon v-if="isAwaitingClient(job)" size="14" color="primary" class="queue-item-icon">mdi-account-edit-outline</v-icon>
                                    <v-progress-circular
                                        v-else-if="['queued','pending','validating','processing'].includes(job.status)"
                                        indeterminate
                                        size="14"
                                        width="2"
                                        color="primary"
                                        class="queue-item-icon"
                                    ></v-progress-circular>
                                    <v-icon v-else-if="job.status === 'completed'" size="14" color="success" class="queue-item-icon">mdi-check-circle</v-icon>
                                    <v-icon v-else-if="job.status === 'failed' || job.status === 'invalid'" size="14" color="error" class="queue-item-icon">mdi-close-circle</v-icon>
                                    <v-icon v-else-if="job.status === 'abandoned'" size="14" color="grey-lighten-1" class="queue-item-icon">mdi-cancel</v-icon>
                                    <v-icon v-else size="14" color="grey-lighten-1" class="queue-item-icon">mdi-clock-outline</v-icon>
                                </template>

                                <v-list-item-title class="queue-item-name queue-item-name--small mp-sensitive" :title="job.experiment_name">
                                    {{ job.experiment_name }}
                                </v-list-item-title>

                                <v-list-item-subtitle class="queue-item-status">
                                    <span v-if="isAwaitingClient(job)" class="text-primary">
                                        {{ t('components.common.queueStatus.status.awaitingClient') }}
                                    </span>
                                    <span v-else :class="job.status === 'failed' ? 'text-error' : (job.status === 'completed' || job.status === 'abandoned') ? 'text-grey' : 'text-primary'">
                                        {{ t(`components.common.queueStatus.status.${['failed','invalid'].includes(job.status) ? 'failed' : job.status === 'completed' ? 'completed' : job.status === 'abandoned' ? 'cancelled' : 'processing'}`) }}
                                    </span>
                                </v-list-item-subtitle>

                                <template v-slot:append>
                                    <DSButton
                                        v-if="isAwaitingClient(job)"
                                        variant="primary"
                                        size="x-small"
                                        class="queue-view-btn"
                                        @click="continueWizard(job)"
                                    >
                                        {{ t('components.common.queueStatus.buttons.continueWizard') }}
                                    </DSButton>
                                    <DSButton
                                        v-else-if="job.status === 'completed'"
                                        variant="primary"
                                        size="x-small"
                                        class="queue-view-btn"
                                        @click="goToExperiment(job.experiment_id, job.id)"
                                    >
                                        {{ t('components.common.queueStatus.buttons.view') }}
                                    </DSButton>
                                </template>
                            </v-list-item>
                        </template>
                    </template>
                </template>

                <!-- Active Jobs -->
                <div v-if="activeJobs.length > 0" class="queue-section-label">{{ t('components.common.queueStatus.sections.processing') }}</div>
                <v-list-item
                    v-for="job in activeJobs"
                    :key="job.id"
                    class="queue-item queue-item--active"
                >
                    <template v-slot:prepend>
                        <v-progress-circular
                            v-if="job.effectiveStatus === 'processing'"
                            indeterminate
                            size="18"
                            width="2"
                            color="primary"
                            class="queue-item-icon"
                        ></v-progress-circular>
                        <v-icon v-else size="18" color="grey-lighten-1" class="queue-item-icon">mdi-clock-outline</v-icon>
                    </template>

                    <v-list-item-title class="queue-item-name mp-sensitive" :title="job.experiment_name">
                        {{ job.experiment_name }}
                    </v-list-item-title>

                    <v-list-item-subtitle class="queue-item-status">
                        {{ job.effectiveStatus === 'processing' ? t('components.common.queueStatus.status.processing') : t('components.common.queueStatus.status.queued') }}
                    </v-list-item-subtitle>

                    <template v-slot:append>
                        <DSButton
                            variant="primary"
                            size="x-small"
                            class="queue-view-btn"
                            @click="goToExperiment(job.experiment_id)"
                        >
                            {{ t('components.common.queueStatus.buttons.view') }}
                        </DSButton>
                    </template>
                </v-list-item>

                <!-- Finished Jobs -->
                <div v-if="finishedJobs.length > 0" class="queue-section-label">{{ t('components.common.queueStatus.sections.finished') }}</div>
                <v-list-item
                    v-for="job in finishedJobs"
                    :key="job.id"
                    :class="['queue-item', 'queue-item--finished', { 'queue-item--unread': !job.read }]"
                >
                    <template v-slot:prepend>
                        <v-icon v-if="job.effectiveStatus === 'completed'" size="18" color="success" class="queue-item-icon">mdi-check-circle</v-icon>
                        <v-tooltip v-if="job.effectiveStatus === 'failed'" location="bottom" max-width="300">
                            <template v-slot:activator="{ props: tooltipProps }">
                                <v-icon v-bind="tooltipProps" size="18" color="error" class="queue-item-icon">mdi-close-circle</v-icon>
                            </template>
                            <span class="mp-sensitive">{{ job.error_message || t('components.common.queueStatus.errors.unknown') }}</span>
                        </v-tooltip>
                    </template>

                    <v-list-item-title class="queue-item-name mp-sensitive" :title="job.experiment_name">
                        {{ job.experiment_name }}
                    </v-list-item-title>

                    <v-list-item-subtitle class="queue-item-status">
                        <span :class="job.effectiveStatus === 'failed' ? 'text-error' : 'text-grey'">
                            {{ job.effectiveStatus === 'completed' ? t('components.common.queueStatus.status.completed') : t('components.common.queueStatus.status.failed') }} {{ formatRelativeTime(job.completed_at) }}
                        </span>
                    </v-list-item-subtitle>

                    <template v-slot:append>
                        <v-btn
                            icon="mdi-close"
                            variant="text"
                            @click="handleDismiss(job.id)"
                            :loading="dismissingId === job.id"
                            :disabled="!!dismissingId"
                            :title="t('components.common.queueStatus.buttons.dismiss')"
                            class="dismiss-btn"
                            size="small"
                        ></v-btn>
                        <DSButton
                            variant="primary"
                            size="x-small"
                            class="queue-view-btn"
                            @click="goToExperiment(job.experiment_id, job.id)"
                        >
                            {{ t('components.common.queueStatus.buttons.view') }}
                        </DSButton>
                    </template>
                </v-list-item>
            </v-list>

            <div v-else class="queue-empty">
                <v-icon size="32" color="grey-lighten-2">mdi-check-all</v-icon>
                <span>{{ t('components.common.queueStatus.empty') }}</span>
            </div>

            <!-- O histórico mora nas Configurações desde a SYN-2767: o sino diz o
                 que reclama atenção agora; a aba "Relatórios" guarda tudo o que a
                 conta gerou. -->
            <div class="queue-footer">
                <DSButton variant="ghost" size="sm" class="queue-footer-link" @click="goToReports">
                    {{ t('components.common.queueStatus.buttons.seeAllReports') }}
                </DSButton>
            </div>
        </v-card>
    </v-menu>

    <ConfirmationDialog ref="confirmDialogRef" />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import { useUserStore } from '@/stores/user';
import { useDataStore } from '@/stores/data';
import { useSnackbar } from '@/composables/useSnackbar';
import { usePptxExportStatus } from '@/composables/usePptxExportStatus';
import { useAnalytics } from '@/composables/useAnalytics';
import { useBatchQueue } from '@/composables/useBatchQueue';
import { DSButton } from '@/design-system/components';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import { useThemeTokens } from '@/design-system/composables/useTheme';
import eventBus from '@/eventBus';
import { isExperimentFullyReady } from '@/utils/experimentReadiness';
import { isWizardPending } from '@/constants/wizardGate';
import { requestWizardResume } from '@/composables/useWizardResume';
import { apiErrorMessage } from '@/utils/apiErrorMessage';

const { t } = useTranslation();
const { getColor } = useThemeTokens();

const props = defineProps({
    showLabel: {
        type: Boolean,
        default: false
    }
});

const userStore = useUserStore();
const dataStore = useDataStore();
const router = useRouter();
const route = useRoute();
const { showSnackbar } = useSnackbar();
// Report export status — surfaced in the bell with a live %. No creative id here:
// the bell is the view over ALL of them, so it takes the registry, and each action
// names the entry it acts on (SYN-1642).
const {
    exports: trackedExports,
    dismiss: dismissExport,
    markSeen: markExportsSeen,
    cancel: cancelExport,
    hydrate: hydrateExport,
    downloadPending: downloadPendingExport,
    openPendingSlides: openPendingSlidesExport,
    openPendingHtml: openPendingHtmlExport,
    activeCount: exportActiveCount,
    unreadCount: exportUnreadCount,
} = usePptxExportStatus();
// An entry with neither progress nor outcome has nothing to say — it exists
// because a click was refused by the cap and rolled back.
const exportEntries = computed(
    () => trackedExports.value.filter((e) => e.active || !!e.result),
);
// A Slides deck whose videos are Drive references: Google transcodes them AFTER
// the upload, and the link is ready before that finishes. Without saying so, the
// user opens the deck, sees "video unavailable" and reads it as a broken feature.
function needsTranscodeNotice(entry) {
    return !!entry.slidesUrl && entry.mediaType === 'video';
}

// Mesmo destino do item "Relatórios" do menu do usuário (AppSidebar
// goToSettingsTab): as Configurações abrem embutidas na listagem, na pasta atual.
function goToReports() {
    menuOpen.value = false;
    const query = { ...route.query, view: 'settings', tab: 'reports' };
    if (route.name === 'MainWithFolder') {
        router.push({ name: 'MainWithFolder', params: { folderId: route.params.folderId }, query });
        return;
    }
    router.push({ name: 'MainWithoutFolder', query });
}

const { track, ANALYTICS_EVENTS } = useAnalytics();

const isClearing = ref(false);
const isMarkingAllRead = ref(false);
const dismissingId = ref(null);
const cancellingId = ref(null);
const menuOpen = ref(false);

const { batchedEntries, retryBatchFailed, dismissBatch } = useBatchQueue();
const expandedBatches = ref(new Set());
const retryingBatchId = ref(null);
const dismissingBatchId = ref(null);
const confirmDialogRef = ref(null);

function toggleBatch(batchId) {
    const next = new Set(expandedBatches.value);
    if (next.has(batchId)) next.delete(batchId);
    else next.add(batchId);
    expandedBatches.value = next;
}

async function handleRetryBatch(batchId) {
    retryingBatchId.value = batchId;
    try {
        await retryBatchFailed(batchId);
    } finally {
        retryingBatchId.value = null;
    }
}

async function handleDismissBatch(batch) {
    // Dismissing a batch with jobs still running cancels them — and that estorna
    // the credit. Confirm first so the user knows the work stops and the refund
    // happens. Fully-terminal batches just clear the notification, no confirm.
    if (batch.activeCount > 0) {
        const result = await confirmDialogRef.value?.show({
            dialogTitle: t('components.common.queueStatus.dismissBatch.title'),
            dialogMessage: t('components.common.queueStatus.dismissBatch.message', { count: batch.activeCount }),
            confirmButtonText: t('components.common.queueStatus.dismissBatch.confirm'),
        });
        if (!result?.confirmed) return;
    }
    dismissingBatchId.value = batch.batchId;
    try {
        await dismissBatch(batch.batchId);
    } catch (error) {
        showSnackbar(apiErrorMessage(error, 'components.common.queueStatus.errors.dismissJob', t), 'error');
    } finally {
        dismissingBatchId.value = null;
    }
}

// SYN-962: header do lote finalizado — anexa "· N cancelados" quando houver
// jobs abandonados, pra o resumo não esconder cancelamentos atrás de "0 com falha".
function batchDoneSummary(batch) {
    let summary = t('components.common.queueStatus.batch.headerDone', {
        time: formatBatchTime(batch.created_at),
        ok: batch.completedCount,
        failed: batch.failedCount,
    });
    if (batch.cancelledCount > 0) {
        summary += t('components.common.queueStatus.batch.cancelledSuffix', { cancelled: batch.cancelledCount });
    }
    return summary;
}

function formatBatchTime(isoString) {
    if (!isoString) return '';
    try {
        return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
}

// Color reference for Vuetify color props
const primaryColor = computed(() => getColor('primary'));

const queueItems = computed(() => userStore.processingQueue);

// SYN-684: o job da fila vira 'completed' quando a ANÁLISE termina, mas o
// criativo só está renderável (ExperimentView sem overlay "Analisando Mídia…")
// quando isExperimentFullyReady — optimizer/insight/attention_map também. O
// `effectiveStatus` rebaixa 'completed' → 'processing' enquanto o dataStore não
// confirma a readiness, pra o sino não dizer "Concluído" antes da hora (alinha
// com o grid). Item ainda não carregado no tree → confia no job.status do backend.
//
// SYN — O PORTÃO DO WIZARD (2026-09-09). Com insight e NeuroGen esperando o fim
// do wizard, `isExperimentFullyReady` é falso por DESIGN enquanto o cliente não
// terminar — e o rebaixamento acima transformava isso em "Em processamento"
// para sempre. O sino ficava com um item eterno dizendo que o sistema trabalha,
// quando quem tem de agir é quem está olhando. `awaiting_client` é o terceiro
// estado que faltava: nem rodando, nem concluído — esperando você.
const AWAITING_CLIENT = 'awaiting_client';
function _effectiveStatus(job) {
    const item = dataStore.findItemById(job.experiment_id);
    // A pergunta do wizard vem ANTES da de prontidão: uma peça que espera o
    // cliente pode estar em qualquer ponto do resto do pipeline, e nenhum deles
    // é a informação que importa nesta linha.
    if (isWizardPending(item)) return AWAITING_CLIENT;
    if (job.status !== 'completed') return job.status;
    if (!item || item.type !== 'experiment') return 'completed';
    return isExperimentFullyReady(item) ? 'completed' : 'processing';
}
const decoratedQueue = computed(() =>
    queueItems.value.map(j => ({ ...j, effectiveStatus: _effectiveStatus(j) }))
);

// Pending/validating included so the dropdown lists every job that is not yet
// finished — keeps the bell consistent with what FileCard surfaces in the grid.
// Ordenado por created_at desc (o mais recente no topo, o mais antigo embaixo)
// para casar com o feedback "a mais embaixo sendo a primeira" e com o FINALIZADOS.
const createdMs = (job) => {
    const ts = job?.created_at ? new Date(job.created_at).getTime() : 0;
    return Number.isNaN(ts) ? 0 : ts;
};
// Batch jobs are rendered as grouped entries; exclude them from individual sections.
const activeJobs = computed(() =>
    decoratedQueue.value
        .filter(j => !j.batch_id && ['queued', 'pending', 'validating', 'processing'].includes(j.effectiveStatus))
        .slice()
        .sort((a, b) => createdMs(b) - createdMs(a))
);
// FINALIZADOS com o mais recente no topo. /jobs/my-queue não garante ordem de
// retorno, então ordenamos por completed_at desc (itens sem completed_at vão
// para o fim) — antes a lista herdava a ordem crua do backend e parecia invertida.
const completedMs = (job) => {
    const ts = job?.completed_at ? new Date(job.completed_at).getTime() : 0;
    return Number.isNaN(ts) ? 0 : ts;
};
const finishedJobs = computed(() =>
    decoratedQueue.value
        .filter(j => !j.batch_id && (j.effectiveStatus === 'completed' || j.effectiveStatus === 'failed'))
        .slice()
        .sort((a, b) => completedMs(b) - completedMs(a))
);
// Orphan jobs — typically created when the tab lost connection during upload
// and the bytes never reached GCS, leaving the job in awaiting_upload with
// a débito unredeemed. Surfaced separately so the user can free the credit
// (cancel → estorno) instead of stacking duplicate uploads.
// Batch jobs (batch_id set) with invalid status are handled inside the batch group.
// ESPERANDO O CLIENTE — a fila que pede ação de quem está lendo. Sai de
// "Em processamento" e de "Finalizados": não é nem uma coisa nem outra, e
// misturá-la com qualquer das duas é a mentira que este bloco veio desfazer.
// Entra também a peça de LOTE: o wizard atende exatamente o envio em lote, e o
// grupo do lote esconderia dentro de si o único item que precisa de alguém.
const awaitingClientJobs = computed(() =>
    decoratedQueue.value
        .filter(j => j.effectiveStatus === AWAITING_CLIENT)
        .slice()
        .sort((a, b) => createdMs(b) - createdMs(a))
);
const orphanJobs = computed(() => queueItems.value.filter(j =>
    !j.batch_id && (j.status === 'awaiting_upload' || j.status === 'invalid')
));
const activeJobCount = computed(() => activeJobs.value.length);
const finishedJobCount = computed(() => finishedJobs.value.length);
const batchActiveCount = computed(() => batchedEntries.value.reduce((sum, b) => sum + b.activeCount, 0));
// Active count comes from the backend queue + the local upload counter — same
// source of truth as the popup. Avoids the false-positive case where videos
// with `optimizer_status === 'processing'` (NeuroGen running silently in the
// background) were being counted from the folderTree but didn't appear in the
// dropdown, leaving the user with "2" on the bell and an empty popup.
const processingCount = computed(() => activeJobCount.value + batchActiveCount.value + userStore.activeUploads + exportActiveCount.value);
// A peça que espera o cliente NÃO entra na contagem do rodopio: rodopio quer
// dizer "o sistema está trabalhando", e aqui ele não está. Ela entra no número,
// que é o que diz "há coisa para você".
const awaitingClientCount = computed(() => awaitingClientJobs.value.length);
// Para o filho de lote, que é renderizado a partir do `status` cru e não passa
// pelo `effectiveStatus`.
const isAwaitingClient = (job) => isWizardPending(dataStore.findItemById(job?.experiment_id));
const isProcessing = computed(() => processingCount.value > 0);
const batchUnreadCount = computed(() =>
    batchedEntries.value
        .filter(b => b.allTerminal)
        .flatMap(b => b.jobs)
        .filter(j => !j.read).length
);
const unreadCount = computed(() =>
    finishedJobs.value.filter(j => !j.read).length +
    batchUnreadCount.value +
    exportUnreadCount.value +
    awaitingClientCount.value
);

const goToExperiment = (experimentId, jobId = null) => {
    if (jobId) {
        userStore.dismissJob(jobId).catch(() => {});
    }
    router.push({
        name: 'ExperimentView',
        params: { experimentId: experimentId },
    });
};

// CONTINUAR O PREENCHIMENTO, do sino. O diálogo do wizard está montado na
// listagem, e o sino pode estar aberto sobre qualquer tela — então aqui se
// deixa o pedido e se navega para lá; a `Main.vue` o consome ao montar (ver
// `useWizardResume`). Nada de abrir modal por cima da tela errada.
const continueWizard = async (job) => {
    if (!job?.experiment_id) return;
    menuOpen.value = false;
    requestWizardResume(job.experiment_id);
    const item = dataStore.findItemById(job.experiment_id);
    const target = item?.folder_id ? `/main/${item.folder_id}` : '/main';
    try { await router.push(target); } catch { /* já estava lá */ }
};

const handleDismiss = async (jobId) => {
    dismissingId.value = jobId;
    try {
        await userStore.dismissJob(jobId);
        // No snackbar needed, UI will update via websocket
    } catch (error) {
        showSnackbar(apiErrorMessage(error, 'components.common.queueStatus.errors.dismissJob', t), 'error');
    } finally {
        dismissingId.value = null;
    }
};

const handleCancelOrphan = async (job) => {
    // The orphan record has `experiment_id` (preferred) or falls back to the
    // job id when the backend doesn't expose the experiment foreign key yet.
    const target = job.experiment_id || job.id;
    if (!target) return;
    cancellingId.value = job.id;
    try {
        await userStore.cancelExperimentJob(target);
        showSnackbar(t('components.common.queueStatus.snackbar.orphanCancelled'), 'success');
    } catch (error) {
        showSnackbar(apiErrorMessage(error, 'components.common.queueStatus.errors.cancelOrphan', t), 'error');
    } finally {
        cancellingId.value = null;
    }
};

const handleMarkAllAsRead = async () => {
    isMarkingAllRead.value = true;
    try {
        const result = await userStore.markAllNotificationsAsRead();
        track(ANALYTICS_EVENTS.NOTIFICATIONS_MARKED_ALL_READ, {
            updated_count: result?.updated_count ?? unreadCount.value,
        });
    } catch (error) {
        showSnackbar(apiErrorMessage(error, 'components.common.queueStatus.errors.markAllRead', t), 'error');
    } finally {
        isMarkingAllRead.value = false;
    }
};

const handleClearAll = async () => {
    isClearing.value = true;
    try {
        await userStore.clearCompletedJobs();
    } catch (error) {
        showSnackbar(apiErrorMessage(error, 'components.common.queueStatus.errors.clearJobs', t), 'error');
    } finally {
        isClearing.value = false;
    }
};

// The bell relied solely on `queue_update` WS pushes + Main.vue's reconnect
// handler. Two failure modes:
//   1) On non-Main pages (ExperimentView, Settings, etc.) it went stale after
//      any WS hiccup since only Main.vue listened to `websocket-reconnected`.
//   2) When a job finished, the backend only sent `status_update` (consumed by
//      Main/ExperimentView for the file grid + experiment screen) — never a
//      `queue_update` — so the badge stayed on "processing" until F5.
// Self-hydrate on mount/reconnect/menu-open AND refetch when a terminal status
// pings the bus.
function refetchQueue() {
    userStore.fetchProcessingQueue();
}

// Bus-triggered refetches (WS terminal pings, reconnects) can arrive in bursts —
// a worker stuck retrying or several jobs finishing back-to-back fired /jobs/my-queue
// dozens of times a second. Throttle those to a sane floor; user-driven refetches
// (menu open, manual refresh button, mount) stay immediate.
let _lastBusRefetch = 0;
const BUS_REFETCH_THROTTLE = 5000;
function refetchQueueThrottled() {
    const now = Date.now();
    if (now - _lastBusRefetch < BUS_REFETCH_THROTTLE) return;
    _lastBusRefetch = now;
    refetchQueue();
}

function handleWsMessage(data) {
    if (!data) return;
    // A report finishing in another tab (or while this one had no tracking) only
    // reaches the bell through the server (SYN-1600).
    if (data.type === 'report_status_update') {
        hydrateExport();
        return;
    }
    if (data.type !== 'status_update') return;
    const terminal = ['completed', 'failed'];
    if (terminal.includes(data.analysis_status) || terminal.includes(data.optimizer_status)) {
        refetchQueueThrottled();
    }
}

watch(menuOpen, (open) => {
    if (open) {
        refetchQueue();
        // Silences the finished exports, EXCEPT any still holding a download:
        // that item needs an action from the user, so merely glancing at the bell
        // must not clear its badge.
        markExportsSeen();
    }
});

onMounted(() => {
    refetchQueue();
    // Rebuilds the export state from /reports/my-queue: the runner's state is a
    // module singleton and does not survive a reload, so without this the user
    // came back to an empty bell while the render was still going.
    hydrateExport();
    eventBus.on('websocket-reconnected', refetchQueueThrottled);
    eventBus.on('websocket-reconnected', hydrateExport);
    eventBus.on('websocket-message', handleWsMessage);
});

onUnmounted(() => {
    eventBus.off('websocket-reconnected', hydrateExport);
    eventBus.off('websocket-reconnected', refetchQueueThrottled);
    eventBus.off('websocket-message', handleWsMessage);
});

function formatRelativeTime(dateString) {
    if (!dateString) return '';
    try {
        let dateStrToParse = String(dateString);

        // This logic ensures that if the backend provides a naive datetime string
        // (common with SQLite), it is correctly interpreted as UTC by JavaScript.
        if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(dateStrToParse) &&
            !dateStrToParse.includes('Z') &&
            !dateStrToParse.includes('+') &&
            !(dateStrToParse.includes('-') && dateStrToParse.lastIndexOf('-') > 7)
        ) {
            dateStrToParse = dateStrToParse.replace(' ', 'T');
            if (!dateStrToParse.endsWith('Z')) {
                 dateStrToParse += 'Z';
            }
        }

        const date = new Date(dateStrToParse);
        if (isNaN(date.getTime())) {
            console.warn("Could not parse date for relative time:", dateString);
            return '';
        }

        const now = new Date();
        const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
        const minutes = Math.round(seconds / 60);
        const hours = Math.round(minutes / 60);
        const days = Math.round(hours / 24);

        if (seconds < 5) return t('components.common.queueStatus.time.now');
        if (seconds < 60) return t('components.common.queueStatus.time.secondsAgo', { seconds });
        if (minutes < 60) return t('components.common.queueStatus.time.minutesAgo', { minutes });
        if (hours < 24) return t('components.common.queueStatus.time.hoursAgo', { hours });
        if (days === 1) return t('components.common.queueStatus.time.yesterday');
        return t('components.common.queueStatus.time.daysAgo', { days });
    } catch (e) {
        console.error("Error formatting relative time:", e);
        return '';
    }
}
</script>

<style scoped>
.queue-btn {
    cursor: pointer !important;
    color: var(--color-text-secondary) !important;
    overflow: visible !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background-color: transparent !important;
    border: none !important;
}

.queue-btn :deep(.v-icon) {
    cursor: pointer;
    color: var(--color-text-secondary) !important;
}

.queue-btn:hover {
    background-color: transparent !important;
    border: none !important;
    color: var(--color-text-primary) !important;
}
.queue-btn-icon-wrap {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}
.queue-icon {
    color: var(--color-text-primary) !important;
    display: flex;
    align-items: center;
    justify-content: center;
}
.queue-badge-spinner {
    position: absolute;
    top: -8px;
    right: -10px;
    display: flex;
    align-items: center;
    justify-content: center;
    /* SYN-701/656: loading único no canto. Removido o anel de pulse que ficava
       ao redor do sino — com o spinner do badge formava o "duplo círculo" e
       sobrepunha a contagem. z-index mantém o badge acima do ícone do sino. */
    z-index: 2;
}
.queue-badge-count {
    position: absolute;
    top: -7px;
    right: -9px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    background: var(--color-primary);
    color: var(--color-text-inverse);
    font-size: var(--font-size-label-small, 12px);
    font-weight: 700;
    line-height: 16px;
    text-align: center;
    border-radius: 8px;
    /* SYN-656: acima do pulse ring */
    z-index: 2;
}

.queue-active-count {
    font-size: var(--font-size-label-small, 12px);
    font-weight: 700;
    color: var(--color-primary);
    line-height: 1;
    font-variant-numeric: tabular-nums;
}

/* Live % inside the export item's progress ring */
.export-pct {
    font-size: var(--font-size-label-small, 12px);
    font-weight: 700;
    color: var(--color-primary);
    line-height: 1;
    font-variant-numeric: tabular-nums;
}
.queue-btn-with-label {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    background: var(--color-tertiary);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s ease;
    color: var(--color-text-primary);
}
.queue-btn-with-label:hover {
    background: var(--color-tertiary-dark);
}
.queue-btn-with-label .v-icon {
    color: var(--color-text-primary);
}
.queue-label-text {
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Card */
.queue-card {
    border-radius: 14px !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.04) !important;
    border: none !important;
    overflow: hidden;
    background-color: var(--color-surface) !important;
}

/* Header */
.queue-footer {
    display: flex;
    justify-content: center;
    padding: 6px 0 8px;
    border-top: 1px solid var(--color-border-subtle);
}

.queue-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px 10px;
    border-bottom: 1px solid var(--color-border-subtle);
}
.queue-header-title {
    font-family: "Noto Sans", sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-primary);
    letter-spacing: 0.01em;
}
.queue-header-actions {
    display: flex;
    align-items: center;
    gap: 2px;
}
.queue-header-btn {
    cursor: pointer !important;
    color: var(--color-text-primary) !important;
    width: 28px !important;
    height: 28px !important;
}
.queue-header-btn:hover {
    cursor: pointer !important;
    color: var(--color-text-primary) !important;
    background-color: var(--color-tertiary) !important;
    border: none !important;
}

/* Section labels */
.queue-section-label {
    font-size: var(--font-size-label-small, 12px);
    font-weight: 600;
    color: var(--color-text-primary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 10px 16px 4px;
}
.queue-section-label--warning {
    color: var(--color-warning);
}
/* A única seção do sino em que a bola está com quem lê: ela usa a cor do
   produto, não o cinza dos relatos de pipeline. */
.queue-section-label--awaiting {
    color: var(--color-primary);
}
.queue-item--awaiting {
    border-left: 2px solid var(--color-primary);
}
.export-transcode-note {
    display: flex;
    gap: 6px;
    align-items: flex-start;
    padding: 2px 16px 10px;
    font-size: var(--font-size-label-small, 12px);
    line-height: 1.4;
    color: var(--color-text-tertiary);
}
.export-transcode-note__icon {
    flex: 0 0 auto;
    margin-top: 1px;
}
.queue-item--orphan {
    background-color: var(--color-warning-light, rgba(255, 152, 0, 0.08)) !important;
}
.queue-item--orphan:hover {
    background-color: var(--color-warning-light, rgba(255, 152, 0, 0.12)) !important;
}

/* List */
.queue-list {
    max-height: 400px;
    overflow-y: auto;
    padding: 4px 6px !important;
}

/* Items */
.queue-item {
    border-radius: 8px !important;
    margin: 1px 0;
    padding: 8px 10px !important;
    min-height: 52px !important;
    transition: background-color 0.12s ease;
}
.queue-item :deep(.v-list-item__overlay) {
    opacity: 0 !important;
}
.queue-item:hover {
    background-color: var(--color-tertiary) !important;
}
.queue-item-icon {
    margin-right: 10px;
}
.queue-item-name {
    font-family: "Noto Sans", sans-serif;
    font-size: 12.5px !important;
    font-weight: 500;
    color: var(--color-text-primary);
    line-height: 1.3;
}
.queue-item-status {
    font-size: var(--font-size-label-small, 12px) !important;
    color: var(--color-text-primary);
    margin-top: 1px;
}
.queue-item--active .queue-item-status {
    color: var(--color-primary);
}

/* View button */
.queue-view-btn {
    font-family: "Noto Sans", sans-serif;
    font-size: var(--font-size-label-small, 12px) !important;
    font-weight: 600 !important;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    border-radius: 6px !important;
    height: 26px !important;
    min-width: 42px !important;
    padding: 0 10px !important;
}

/* Dismiss */
.dismiss-btn {
    cursor: pointer !important;
    color: var(--color-text-primary) !important;
    width: 24px !important;
    height: 24px !important;
    margin-right: 4px;
}
.dismiss-btn:hover {
    cursor: pointer !important;
    color: var(--color-text-primary) !important;
    background-color: var(--color-tertiary) !important;
    border: none !important;
}

/* Unread indicator */
.queue-item--unread {
    background-color: var(--color-primary-opacity-10, rgba(var(--color-primary-rgb, 0, 0, 0), 0.06)) !important;
    border-left: 3px solid var(--color-primary) !important;
}
.queue-item--unread .queue-item-name {
    font-weight: 600;
}

/* Finished items */
.queue-item--finished .queue-item-name {
    color: var(--color-text-primary);
}

/* Batch entries */
.queue-item--batch {
    background-color: var(--color-tertiary-light, rgba(0,0,0,0.02)) !important;
}
/* Batch header summary can be long ("… · 0 falharam") — let it wrap to a second
   line instead of truncating with Vuetify's default v-list-item-title ellipsis. */
.queue-item--batch .queue-item-name {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
}
.queue-item--batch-child {
    padding-left: 28px !important;
    min-height: 40px !important;
}
.queue-item-name--small {
    font-size: var(--font-size-label-small, 12px) !important;
}

/* Empty state */
.queue-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 28px 16px;
    color: var(--color-text-tertiary);
    font-size: 12.5px;
    font-weight: 500;
}
</style>
